import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useContext } from "react";
import { GlobalContext } from "../../GlobalProvider";
import { getEntitiesByAppId, deleteEntityById } from "../../api/api";
import { useNavigate } from "react-router-dom";

const EntityList = () => {
  const { currentApp } = useContext(GlobalContext); // Get currentApp from GlobalProvider
  const appId = currentApp?.id; // Extract appId from currentApp
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // State for delete confirmation
  const navigate = useNavigate();

  const fetchEntities = async () => {
    if (!appId) {
      setError("App ID is not set.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getEntitiesByAppId(appId, {
        page: page + 1,
        limit: rowsPerPage,
      });
      setEntities(response.data.data);
      setTotal(response.data.total);
    } catch (err) {
      setError(err.message || "Failed to fetch entities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, [appId, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };

  const handleAddEntity = () => {
    navigate("/create-entity"); // Navigate to the Add Entity page
  };

  const handleEditEntity = (entityId) => {
    navigate(`/edit-entity/${entityId}`); // Navigate to the edit entity page
  };

  const handleDeleteEntity = async () => {
    try {
      await deleteEntityById(deleteConfirm); // Replace with your API call
      fetchEntities(); // Refresh the data
      setDeleteConfirm(null); // Close the confirmation dialog
    } catch (err) {
      console.error("Failed to delete entity:", err);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : entities.length === 0 ? (
        <Box textAlign="center" mt={3}>
          <Typography variant="h6">No Entities Found</Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
            Create your first entity to start managing data for this app.
          </Typography>
          <Button variant="contained" color="primary" onClick={handleAddEntity}>
            Create New Entity
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" color="primary" onClick={handleAddEntity}>
              Add Entity
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Keys</TableCell>
                  <TableCell>No of Sensors</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entities.map((entity) => (
                  <TableRow key={entity._id}>
                    <TableCell>{entity.name}</TableCell>
                    <TableCell>{entity.entityTypeId?.name || "N/A"}</TableCell>
                    <TableCell>
                      {entity.keys.map((key) => (
                        <div key={key._id}>
                          <strong>{key.keyName}:</strong> {key.value}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>
                      {entity.sensors.length}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleEditEntity(entity._id)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setDeleteConfirm(entity._id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this entity? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={handleDeleteEntity}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntityList;
